import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { guestRewardsApi } from "@/api/guest/guestRewardsApi.js";
import GuestLayout from "@/components/guest/GuestLayout.jsx";
import { Button } from "@/components/ui/button";
import { useSession } from "@/hooks/useSession.js";
import { getApiData, getApiMessage } from "@/utils/guestRewardsFormat.js";

const GuestRegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session, hotel, room, loading: sessionLoading } = useSession();
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    sendVerificationEmail: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!sessionLoading && !session) navigate("/guest/expired", { replace: true });
  }, [sessionLoading, session, navigate]);

  useEffect(() => {
    if (!session) return;
    setForm((current) => ({
      ...current,
      fullName: current.fullName || session.guestName || "",
      phoneNumber: current.phoneNumber || session.guestPhone || "",
    }));
  }, [session]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.fullName.trim()) {
      toast.error("Please enter your full name.");
      return;
    }

    try {
      setSaving(true);
      const response = await guestRewardsApi.registerProfile({
        fullName: form.fullName,
        email: form.email,
        phoneNumber: form.phoneNumber,
        sendVerificationEmail: form.sendVerificationEmail,
      });
      const data = getApiData(response);
      const profileResponse = await guestRewardsApi.getProfile();
      const profileState = getApiData(profileResponse);
      if (profileState?.requiresRegistration || profileState?.RequiresRegistration) {
        throw new Error("Registration completed, but the profile was not linked to this guest session.");
      }
      toast.success(data?.message || "Registration completed successfully.");
      navigate(getSafeReturnPath(searchParams.get("returnTo")) || "/guest/profile", { replace: true });
    } catch (error) {
      toast.error(getApiMessage(error, "Unable to register your profile."));
    } finally {
      setSaving(false);
    }
  };

  if (sessionLoading) {
    return <FullScreenLoading />;
  }

  if (!session) return null;

  return (
    <>
      <Helmet>
        <title>Register - Guest Portal</title>
      </Helmet>
      <GuestLayout hotel={hotel} room={room} session={session}>
        <div className="mx-auto max-w-2xl space-y-5">
          <BackButton onClick={() => navigate("/guest/home")} />
          <section className="rounded-3xl border border-white/75 bg-white/90 p-5 shadow-[0_22px_52px_rgba(15,23,42,0.18)] backdrop-blur-xl sm:p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#923536]/10 text-[#923536]">
                <UserPlus className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Register your profile</h1>
                <p className="mt-1 text-sm text-slate-600">
                  Save your stay history, earn loyalty points, and access memberships for future visits.
                </p>
              </div>
            </div>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <GuestInput
                label="Full name"
                value={form.fullName}
                onChange={(value) => setForm((current) => ({ ...current, fullName: value }))}
                required
              />
              <GuestInput
                label="Email address"
                type="email"
                value={form.email}
                onChange={(value) => setForm((current) => ({ ...current, email: value }))}
              />
              <GuestInput
                label="Phone number"
                value={form.phoneNumber}
                onChange={(value) => setForm((current) => ({ ...current, phoneNumber: value }))}
              />
              <label className="flex items-center gap-3 rounded-2xl border bg-white px-4 py-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.sendVerificationEmail}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, sendVerificationEmail: event.target.checked }))
                  }
                  className="h-4 w-4 accent-[#923536]"
                />
                Send a verification email if an email address is provided.
              </label>
              <Button type="submit" disabled={saving} className="h-12 w-full bg-[#923536] text-white hover:bg-[#7f2d2e]">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Register
              </Button>
            </form>
          </section>
        </div>
      </GuestLayout>
    </>
  );
};

const FullScreenLoading = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-12 w-12 animate-spin text-primary" />
  </div>
);

const BackButton = ({ onClick }) => (
  <Button type="button" variant="ghost" onClick={onClick} className="bg-white/85 text-slate-800 hover:bg-white">
    <ArrowLeft className="mr-2 h-4 w-4" />
    Back
  </Button>
);

const GuestInput = ({ label, value, onChange, type = "text", required = false }) => (
  <label className="block space-y-2">
    <span className="text-sm font-medium text-slate-800">
      {label}
      {required ? <span className="text-[#923536]"> *</span> : null}
    </span>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none transition focus:border-[#923536] focus:ring-2 focus:ring-[#923536]/15"
    />
  </label>
);

const getSafeReturnPath = (value) => {
  if (!value || !value.startsWith("/guest/")) return "";
  return value.startsWith("//") ? "" : value;
};

export default GuestRegisterPage;
